/* ============================================================
   SAM-198 — full-page service editor.

   Replaces the old ServiceDetailDrawer side panel: the same information
   (general info, booking type, operating hours, resources, closures, role
   pricing / tier groups, effective-schedule preview, booking-engine sync,
   cancellation policy) laid out as a full page — section nav on the left,
   sectioned cards, sticky save bar — per
   `Documentation/SAM AdminBusinessUI/SAM Admin - Design System.html` §11
   (the master-detail shell) and the "Details / Scheduling & Capacity /
   Pricing / Policies" sectioned form in
   `Documentation/SAM AdminBusinessUI/SAM Admin - Business.html` +
   offer-flow.js.

   All form state, validation and the save payload come from
   `useServiceEditor` (src/hooks/useServiceEditor.ts) — this file is
   presentation only, built with Tailwind + src/styles/tokens.css +
   src/components/ui/* (no baseui / styletron-react, per the active
   BaseUI/Styletron freeze).
   ============================================================ */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Briefcase, Building2, Check, Loader2, Save, Zap,
} from 'lucide-react';
import { useBusinessContext } from '../../context/BusinessContext';
import { ReadOnlyFence } from '../../components/ReadOnlyFence';
import { useFdCan } from '../../hooks/useFdCan';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { useBusinessById } from '../../hooks/useBusinessCrud';
import { useBusinesses, type Business } from '../../hooks/useBusinessAccessType';
import { useServiceById, useServiceBusinessId, useServiceRow, serviceRowFields, isBookingEngineSyncEnabled, useEngineSyncStatus } from '../../hooks/useServices';
import {
  useServiceEditor, STATUS_OPTIONS, SUBTYPE_OPTIONS,
} from '../../hooks/useServiceEditor';
import { S3ImageUpload } from '../../components/S3ImageUpload';
import { BookingEngineSyncStatus } from '../../components/ui/BookingEngineSyncStatus';
import { EngineSyncBadge } from '../../components/ui/EngineConnect';
import { engineSyncState } from '../../utils/engineMapping';
import { SchedulingSection } from './sections/SchedulingSection';
import { UnitsSection } from './sections/UnitsSection';
import { PricingSection } from './sections/PricingSection';
import { SessionsSection } from './sections/SessionsSection';
import { PoliciesSection } from './sections/PoliciesSection';
import { computeHeadcountWarning, formatHeadcountWarning } from './headcountWarning';
import { Notice } from '../../components/ui/Notice';
import { Modal } from '../../components/ui/Modal';
import { ServiceLivePreview } from './ServiceLivePreview';
import { useCreateServiceFlow } from './create/CreateServiceFlow';
import { NewServiceWizard } from './create/NewServiceWizard';
import { IconSelect } from '../../components/ui/IconSelect';
import { SERVICE_BADGE_OPTIONS, badgeOption, suggestedBadge, type ServiceBadgeKey } from '../../utils/serviceBadge';
import { CREATE_SERVICE_TYPES, createTypeOfSubtype, subtypeLabel } from './create/serviceCreateTaxonomy';
import { aheadToParts, pricingCurrency, resourceWord } from './editorV2';
import { CURRENCY_SYMBOL } from '../../utils/money';

// --- Section nav ---

type SectionKey = 'details' | 'scheduling' | 'sessions' | 'units' | 'pricing' | 'policies' | 'engine';

interface SectionDef {
  key: SectionKey;
  label: string;
}

// SAM-198 editor v2 (mock 3 v2, approved 2026-10-01) — section order: Basic details → Booking &
// hours → Pricing → Resources → Policies & add-ons. Pricing is the only place with prices and
// Resources (formerly Units / "Cabanas") only lists what gets booked, so it is always available —
// any setup can have rows (tables, courts, tee starts…). Sessions and Booking engine are this
// admin's own sections (no mock counterpart) and keep their gating: Sessions only for a 'fixed'
// service (SAM-546), Booking engine only once the service exists and the pilot flag is on
// (SAM-194).
function useSections(
  mode: 'create' | 'edit',
  bookingType: 'fixed' | 'day' | 'flexible',
): SectionDef[] {
  return useMemo(() => {
    const secs: SectionDef[] = [
      { key: 'details', label: 'Basic details' },
      { key: 'scheduling', label: 'Booking & hours' },
    ];
    if (bookingType === 'fixed') {
      secs.push({ key: 'sessions', label: 'Sessions' });
    }
    secs.push(
      { key: 'pricing', label: 'Pricing' },
      { key: 'units', label: 'Resources' },
      { key: 'policies', label: 'Policies & add-ons' },
    );
    if (mode === 'edit' && isBookingEngineSyncEnabled()) {
      secs.push({ key: 'engine', label: 'Booking engine' });
    }
    return secs;
  }, [mode, bookingType]);
}

// --- Small field primitives (Tailwind + tokens, per DS §08 Forms & inputs) ---

/** Small grey pill for a setting that is saved but not read by bookings yet — replaces the old
 * amber banners that named tickets and engine fields. */
export const PendingPill: React.FC = () => (
  <span
    className="text-[10px] font-semibold align-middle"
    style={{
      marginLeft: 6, padding: '1px 7px', borderRadius: 999,
      background: 'var(--line-subtle)', color: 'var(--ink-500)', whiteSpace: 'nowrap',
    }}
  >
    Not active yet
  </span>
);

export const Field: React.FC<{
  label: string;
  caption?: React.ReactNode;
  required?: boolean;
  /** Saved, but nothing reads it yet — shows a "Not active yet" pill after the label. */
  pending?: boolean;
  error?: string;
  children: React.ReactNode;
}> = ({ label, caption, required, pending, error, children }) => (
  <div className={error ? 'fc err' : 'fc'} style={{ marginBottom: 14 }}>
    <label>
      {label}
      {required && <span className="req"> *</span>}
      {pending && <PendingPill />}
    </label>
    {children}
    {caption && !error && <div className="cap">{caption}</div>}
    {error && <div className="msg" style={{ display: 'block' }}>{error}</div>}
  </div>
);

export const Grid2: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">{children}</div>
);

export function SectionCard({
  title, icon, children,
}: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <section
      className="rounded-12 mb-4 bg-white min-w-0"
      style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-12)' }}
    >
      <div
        className="flex items-center gap-2 px-4 py-3"
        style={{ borderBottom: '1px solid var(--line-subtle)' }}
      >
        <span style={{ color: 'var(--tenant-primary, #111827)' }}>{icon}</span>
        <h2 className="text-[13px] font-semibold" style={{ color: 'var(--ink-900)' }}>{title}</h2>
      </div>
      <div className="p-3 sm:p-4 min-w-0">{children}</div>
    </section>
  );
}

export const ServiceEditorPage: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams<{ serviceId?: string }>();
  const [searchParams] = useSearchParams();
  const isCreate = !params.serviceId || params.serviceId === 'new';
  const mode: 'create' | 'edit' = isCreate ? 'create' : 'edit';

  const { business } = useBusinessContext();

  const { data: businessesData } = useBusinesses();
  const businesses: Business[] = useMemo(() => {
    const raw = businessesData as Business[] | { data?: Business[] } | undefined;
    const list = Array.isArray(raw) ? raw : (raw && 'data' in raw && Array.isArray(raw.data) ? raw.data : []);
    return [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [businessesData]);

  // A "New service" from within a specific business (e.g. the Business Detail panel's Services
  // tab) passes ?businessId= so the new service is pre-scoped to it, same as the old drawer's
  // `businessId` prop.
  const businessIdParam = searchParams.get('businessId');
  // Editing: the service's own business (crud row), never the session's — see useServiceBusinessId.
  const ownerQuery = useServiceBusinessId(isCreate ? undefined : params.serviceId);
  const contextBusinessId = (!isCreate && ownerQuery.data) || businessIdParam || business?.id || null;
  // Mock 2 v2 (approved 2026-10-01): a super admin (Cap Cana / Xhinola admin) confirms the
  // business first, pre-filled from the business they came from; a business admin always creates
  // in their own business and goes straight to the type (Rodrigo, 2026-09-28).
  const permissions = useUserPermissions();
  // The tenant Front Desk views services but does not change them (specs/front-desk-tenant-permisos.md).
  const canEditService = useFdCan('business', 'edit');
  const canPickBusiness = isCreate && permissions.isAdmin && businesses.length > 1;

  const {
    data: loadedService, isLoading: isServiceLoading, isError: isServiceError,
  } = useServiceById(isCreate ? undefined : params.serviceId);
  // SAM-1009: /services/description leaves out the media and the active flag; they come from the crud row.
  const { data: serviceRow } = useServiceRow(isCreate ? undefined : params.serviceId);
  const service = useMemo(
    () => (loadedService ? { ...loadedService, ...serviceRowFields(serviceRow) } : loadedService),
    [loadedService, serviceRow],
  );

  const editor = useServiceEditor({
    mode,
    service: service || null,
    businessId: contextBusinessId,
    resetKey: params.serviceId,
  });
  const { formData, updateField, updateSubtype } = editor;
  // SAM-198: /services/description does not return the engine mapping, so the header reads the
  // backend's own verdict (same cached query as the "Booking engine" section's status block).
  const engineStatus = useEngineSyncStatus(mode === 'edit' ? service?.id : null);
  const engineState = engineSyncState(service, engineStatus.data);
  // The owning business's row (shared, cached query) — its name for the header chip even when it
  // is not in this admin's business list.
  const { data: ownerRows } = useBusinessById(formData.business_id || null);

  const sections = useSections(mode, formData.booking_type);
  const [activeSection, setActiveSection] = useState<SectionKey>('details');
  useEffect(() => {
    if (!sections.find((s) => s.key === activeSection)) setActiveSection('details');
  }, [sections, activeSection]);

  // SAM-1073 — creating is the four-step wizard (create/NewServiceWizard.tsx); saving it opens this
  // editor on the new service. Editing is always the form below.
  const createFlow = useCreateServiceFlow(editor, isCreate);
  const inForm = !isCreate;

  // Setup is chosen once, in the create flow, and shown as a chip in the header — not as a
  // dropdown inside Basic Details. "Change setup" goes back to the picker while creating; on an
  // existing service it opens the same Offering Type select in a small panel under the header.
  const [showSetupPanel, setShowSetupPanel] = useState(false);
  const setupOption = SUBTYPE_OPTIONS.find((o) => o.id === formData.subtype);
  const setupType = createTypeOfSubtype(formData.subtype);
  const setupName = createFlow.pickedLabel
    || (setupType ? subtypeLabel(setupType, formData.subtype) : setupOption?.label) || 'Custom';
  const setupLabel = setupType
    ? `${CREATE_SERVICE_TYPES.find((t) => t.k === setupType)?.n} · ${setupName}`
    : setupName;
  const onChangeSetup = () => setShowSetupPanel((v) => !v);

  // --- Unsaved-changes guard ---
  // The app routes with a plain <BrowserRouter>/<Routes> (see src/main.tsx), not a data router,
  // so React Router's useBlocker (which needs a data router) isn't available here. `beforeunload`
  // covers a hard refresh/tab-close/external navigation; the in-page dialog below covers this
  // page's own Cancel/back controls. A click on an unrelated sidebar link while dirty is still
  // not intercepted (needs createBrowserRouter — known gap).
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!editor.isDirty) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [editor.isDirty]);

  const [confirmLeave, setConfirmLeave] = useState(false);
  // Back to wherever the admin came from. Opened by a direct link (no in-app history entry),
  // `navigate(-1)` would leave the app and the dirty `beforeunload` guard would swallow it, so go
  // to the business's service list instead.
  const leave = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate(businessIdParam ? `/services?businessId=${encodeURIComponent(businessIdParam)}` : '/services');
  };
  const handleBack = () => {
    if (editor.isDirty) setConfirmLeave(true);
    else leave();
  };

  const handleSave = () => {
    editor.save((savedId) => {
      setConfirmLeave(false);
      if (mode === 'create' && savedId) {
        navigate(`/services/${savedId}`, { replace: true });
      }
    });
  };

  const handleDelete = () => {
    editor.remove(() => navigate('/services'));
  };

  // SAM-198 — non-blocking headcount warning (Units vs. Pricing tier-group caps), shown under the
  // header so it is visible from any section — see headcountWarning.ts for the rule.
  const headcountWarning = computeHeadcountWarning(formData.resources, formData.tier_groups);

  const businessName = businesses.find((b) => String(b.id) === String(formData.business_id))?.name
    || ownerRows?.[0]?.name
    || (formData.business_id && formData.business_id === business?.id ? business?.name : undefined);
  const displayName = formData.name_en || formData.name_es
    || (mode === 'create'
      ? (inForm ? `New ${createFlow.pickedNoun || 'service'}` : `New service${businessName ? ` · ${businessName}` : ''}`)
      : 'Service');

  // Rail state: a section is "ready" when the thing it exists for is filled in.
  const sectionDone: Partial<Record<SectionKey, boolean>> = {
    details: formData.name_en.trim() !== '',
    scheduling: true,
    units: formData.resources.length > 0,
    pricing: formData.booking_options.length === 0
      || formData.booking_options.every((o) => String(o.price).trim() !== ''),
    policies: true,
  };
  // One-line summary under each rail item (mock 3 v2).
  const word = resourceWord(formData.subtype);
  const ahead = aheadToParts(formData.booking_window_end);
  const sym = CURRENCY_SYMBOL[pricingCurrency(formData)] || pricingCurrency(formData);
  const sectionHint: Partial<Record<SectionKey, string>> = {
    details: 'Name, description, media',
    scheduling: `${ahead.value ? `${ahead.value} ${ahead.unit} ahead` : 'No limit ahead'} · ${formData.hours_source === 'business' ? 'business hours' : 'custom hours'}`,
    pricing: formData.price_unit === 'unit'
      ? (formData.unit_price ? `${sym} ${formData.unit_price} per ${word.singular}` : `Per ${word.singular}`)
      : formData.price_unit === 'event' ? 'Per event size'
        : `Per guest${formData.rate_groups.length ? ` · ${formData.rate_groups.length + 1} rates` : ''}`,
    units: formData.resources.length === 0
      ? '1 pool (no named resources)'
      : `${formData.resources.length} ${formData.resources.length === 1 ? word.singular : word.plural}`,
    policies: `${formData.add_ons.length} add-on${formData.add_ons.length === 1 ? '' : 's'}`,
  };
  const readyCount = sections.filter((s) => sectionDone[s.key]).length;
  const todo: string[] = [];
  if (!formData.name_en.trim()) todo.push('Name in English');
  if (isCreate && !formData.business_id) todo.push('Business');
  if (formData.booking_options.some((o) => String(o.price).trim() === '')) todo.push('A price for every booking option');

  const leaveModal = (
      <Modal
        isOpen={confirmLeave}
        onClose={() => setConfirmLeave(false)}
        title={mode === 'create' ? 'Discard this new service?' : 'Discard your changes?'}
        footer={(
          <>
            <button type="button" className="btn ghost" style={{ color: 'var(--danger)' }} onClick={leave}>
              Discard
            </button>
            <button type="button" className="btn ghost" onClick={() => setConfirmLeave(false)}>
              Keep editing
            </button>
            <button
              type="button"
              className="btn primary"
              onClick={handleSave}
              disabled={!editor.isFormValid || editor.isSaving}
            >
              {mode === 'create' ? 'Create service' : 'Save changes'}
            </button>
          </>
        )}
      >
        <p className="text-[14px]" style={{ color: 'var(--ink-700)' }}>
          {mode === 'create'
            ? 'Nothing has been created yet. Create it now, or discard it.'
            : 'You have unsaved changes on this service.'}
        </p>
      </Modal>
  );

  if (isCreate) {
    return (
      <>
      <NewServiceWizard
        editor={editor}
        flow={createFlow}
        businessName={businessName}
        businesses={businesses}
        canPickBusiness={canPickBusiness}
        onCreate={handleSave}
        onCancel={handleBack}
      />
      {leaveModal}
      </>
    );
  }

  if (mode === 'edit' && (isServiceLoading || ownerQuery.isLoading)) {
    return (
      <div className="flex items-center justify-center h-full text-sm" style={{ color: 'var(--ink-500)' }}>
        <Loader2 className="animate-spin mr-2" size={16} /> Loading service…
      </div>
    );
  }

  if (mode === 'edit' && (isServiceError || !service)) {
    return (
      <div className="p-8 text-sm" style={{ color: 'var(--ink-500)' }}>
        <p>Could not load this service.</p>
        <button type="button" className="btn ghost mt-3" onClick={() => navigate('/services')}>
          <ArrowLeft size={14} /> Back to Services
        </button>
      </div>
    );
  }

  const chip: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600,
    padding: '2px 10px', borderRadius: 999,
  };

  return (
    // min-w-0 + w-full: on phones the layout grid's single 1fr column would otherwise grow to the
    // widest child and push the page past the viewport.
    <div className="flex flex-col h-full min-w-0 w-full" style={{ background: 'var(--surface)' }}>
      {/* ======================== PAGE HEADER (sticky actions) ======================== */}
      {/* Phones: two rows — back + title + chips, then status + Cancel/Save. md+: one row. */}
      <header
        className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3 px-4 md:px-6 py-3 shrink-0 bg-white min-w-0"
        style={{ borderBottom: '1px solid var(--line)', position: 'relative', zIndex: 10 }}
      >
        <div className="flex items-start gap-3 min-w-0 md:flex-1">
        <button
          type="button"
          onClick={handleBack}
          className="ico-btn"
          aria-label="Back to Services"
          style={{
            width: 36, height: 36, borderRadius: 'var(--r-8)', border: '1px solid var(--line)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#fff',
            color: 'var(--ink-700)', flexShrink: 0,
          }}
        >
          <ArrowLeft size={16} />
        </button>

        <div className="min-w-0 flex-1">
          <h1 className="text-[17px] md:text-[18px] font-semibold leading-snug break-words md:truncate" style={{ color: 'var(--ink-900)' }}>
            {displayName}
          </h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {inForm && businessName && (
              <span style={{ ...chip, background: 'var(--line-subtle)', color: 'var(--ink-700)', maxWidth: '100%' }}>
                <Building2 size={12} className="shrink-0" /><span className="truncate">{businessName}</span>
              </span>
            )}
            {inForm && (
              <>
                <span style={{ ...chip, background: 'color-mix(in srgb, var(--tenant-primary, #005488) 10%, white)', color: 'var(--tenant-primary, #005488)', maxWidth: '100%' }}>
                  <span className="truncate">{setupLabel}</span>
                </span>
                <button type="button" className="text-[12px] font-semibold underline" style={{ color: 'var(--tenant-primary, #005488)', border: 'none', background: 'none', padding: 0 }} onClick={onChangeSetup}>
                  Change setup
                </button>
              </>
            )}
            {mode === 'edit' && isBookingEngineSyncEnabled() && service?.id && !engineStatus.isError && (
              <EngineSyncBadge
                state={engineState}
                serviceId={String(service.id)}
                disabled={editor.isSaving || editor.isDirty}
                disabledReason={editor.isSaving ? 'Saving…' : 'Save your changes first: the engine gets the saved service'}
              />
            )}
          </div>
        </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap md:flex-nowrap min-w-0">
        {inForm && (
          <>
            <ReadOnlyFence section="business">
            <div role="group" aria-label="Status" className="flex p-0.5 shrink-0" style={{ background: 'var(--line-subtle)', borderRadius: 'var(--r-8)' }}>
              {STATUS_OPTIONS.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  aria-pressed={formData.status === o.id}
                  onClick={() => updateField('status', o.id)}
                  className="text-[12px] font-semibold px-3 py-1.5"
                  style={{
                    border: 'none',
                    borderRadius: 'var(--r-8)',
                    background: formData.status === o.id ? '#fff' : 'transparent',
                    color: formData.status === o.id ? 'var(--ink-900)' : 'var(--ink-500)',
                    boxShadow: formData.status === o.id ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                  }}
                >
                  {o.label}
                </button>
              ))}
            </div>
            </ReadOnlyFence>
            <span
              className={`text-xs items-center gap-1.5 whitespace-nowrap ${editor.isDirty ? 'inline-flex' : 'hidden lg:inline-flex'}`}
              style={{ color: editor.isDirty ? 'var(--warn-fg)' : 'var(--ink-400)' }}
            >
              {editor.isDirty && <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--warn-fg)' }} />}
              <span className={editor.isDirty ? 'hidden sm:inline' : ''}>
                {editor.isDirty ? 'Unsaved changes' : mode === 'edit' ? 'All changes saved' : ''}
              </span>
            </span>
          </>
        )}
        {inForm && editor.seasonBlockMessage && (
          <span role="alert" data-testid="save-blocked-seasons" className="text-xs basis-full md:basis-auto" style={{ color: 'var(--danger, #c0392b)' }}>
            Seasons: {editor.seasonBlockMessage}
          </span>
        )}
        {inForm && editor.saveError && (
          <span role="alert" data-testid="save-error" className="text-xs basis-full md:basis-auto" style={{ color: 'var(--danger, #c0392b)' }}>
            Could not save: {editor.saveError}
          </span>
        )}
        {inForm && editor.penaltyPercentIssue && (
          <span role="alert" data-testid="save-blocked-penalty" className="text-xs basis-full md:basis-auto" style={{ color: 'var(--danger, #c0392b)' }}>
            Cancellation penalty: {editor.penaltyPercentIssue}
          </span>
        )}
        <span className="flex-1 md:hidden" />
        <button type="button" className="btn ghost shrink-0" onClick={handleBack} disabled={editor.isSaving}>
          Cancel
        </button>
        {inForm && canEditService && (
          <button
            type="button"
            className="btn primary shrink-0"
            onClick={handleSave}
            disabled={!editor.isFormValid || editor.isSaving}
          >
            <Save size={14} />
            {editor.isSaving ? 'Saving…' : mode === 'create' ? 'Create' : 'Save'}
            <span className="hidden sm:inline">{editor.isSaving ? '' : mode === 'create' ? ' service' : ' changes'}</span>
          </button>
        )}
        </div>
      </header>

      {showSetupPanel && !isCreate && (
        <div className="px-6 py-3 shrink-0 bg-white" style={{ borderBottom: '1px solid var(--line)' }}>
          <div className="max-w-xl">
            <Field label="Setup" caption={setupOption?.caption}>
              <select
                value={formData.subtype}
                onChange={(e) => updateSubtype((e.target.value || '') as '' | typeof formData.subtype)}
              >
                {SUBTYPE_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </Field>
          </div>
        </div>
      )}

      {headcountWarning && inForm && (
        <div className="px-6 pt-2 shrink-0">
          <Notice variant="warn">
            {formatHeadcountWarning(headcountWarning)}{' '}
            {activeSection !== 'units' && (
              <button type="button" className="underline font-medium" onClick={() => setActiveSection('units')}>
                Review resources
              </button>
            )}
          </Notice>
        </div>
      )}

      {/* ======================== CREATE FLOW: (business) · type · setup ======================== */}
      {/* ======================== BODY: section rail · form · live preview ======================== */}
      {inForm && (
        <div className="flex-1 min-h-0 min-w-0 flex">
          <nav
            aria-label="Service editor sections"
            className="hidden md:flex flex-col gap-1 shrink-0 bg-white overflow-y-auto"
            style={{ width: 260, borderRight: '1px solid var(--line)', padding: '20px 12px' }}
          >
            <div className="text-[11px] font-semibold uppercase tracking-wide px-3 pb-2" style={{ color: 'var(--ink-500)' }}>
              {readyCount} of {sections.length} ready
            </div>
            {sections.map((s) => {
              const on = activeSection === s.key;
              const done = sectionDone[s.key];
              return (
                <button
                  key={s.key}
                  type="button"
                  aria-current={on ? 'page' : undefined}
                  onClick={() => setActiveSection(s.key)}
                  className="flex items-center gap-2.5 text-left text-[13px] font-semibold px-3 py-2"
                  style={{
                    border: 'none',
                    borderRadius: 'var(--r-8)',
                    background: on ? 'color-mix(in srgb, var(--tenant-primary, #005488) 10%, white)' : 'transparent',
                    color: on ? 'var(--tenant-primary, #005488)' : 'var(--ink-700)',
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      width: 18, height: 18, borderRadius: '50%', flexShrink: 0, display: 'inline-flex',
                      alignItems: 'center', justifyContent: 'center',
                      background: done ? 'var(--ok-bg)' : 'transparent',
                      border: done ? 'none' : '2px solid var(--line)',
                      color: 'var(--ok-fg)',
                    }}
                  >
                    {done && <Check size={11} strokeWidth={3} />}
                  </span>
                  <span className="flex flex-col items-start min-w-0">
                    <span>{s.label}</span>
                    {sectionHint[s.key] && (
                      <span className="text-[11px] font-normal truncate max-w-[180px]" style={{ color: 'var(--ink-500)' }}>{sectionHint[s.key]}</span>
                    )}
                  </span>
                </button>
              );
            })}
            {todo.length > 0 && (
              <div className="mt-auto text-[12px] p-3" style={{ background: 'var(--surface)', borderRadius: 'var(--r-8)', color: 'var(--ink-500)' }}>
                <div className="font-semibold mb-1" style={{ color: 'var(--ink-900)' }}>To publish</div>
                {todo.map((t) => <div key={t}>· {t}</div>)}
              </div>
            )}
          </nav>

          <main className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden">
            <div className="max-w-3xl mx-auto px-3 py-4 sm:p-6 pb-16 min-w-0">
              {/* Narrow screens have no rail — fall back to the underline tabs. */}
              <nav className="osec-nav md:hidden" aria-label="Service editor sections">
                {sections.map((s) => (
                  <button key={s.key} type="button" className={activeSection === s.key ? 'on' : ''} onClick={() => setActiveSection(s.key)}>
                    {s.label}
                  </button>
                ))}
              </nav>

              <h2 className="text-[22px] font-bold mb-4" style={{ color: 'var(--ink-900)' }}>
                {sections.find((s) => s.key === activeSection)?.label}
              </h2>
              <ReadOnlyFence section="business">
              {activeSection === 'details' && <DetailsSection editor={editor} />}
              {activeSection === 'scheduling' && (
                <SchedulingSection editor={editor} onGoResources={() => setActiveSection('units')} />
              )}
              {activeSection === 'sessions' && <SessionsSection editor={editor} />}
              {activeSection === 'units' && <UnitsSection editor={editor} />}
              {activeSection === 'pricing' && <PricingSection editor={editor} />}
              {activeSection === 'policies' && (
                <PoliciesSection editor={editor} mode={mode} onDelete={handleDelete} />
              )}
              {activeSection === 'engine' && service && (
                <SectionCard title="Booking engine" icon={<Zap size={14} />}>
                  <BookingEngineSyncStatus serviceId={service.id} />
                </SectionCard>
              )}
              </ReadOnlyFence>
            </div>
          </main>

          <aside
            aria-label="Live preview"
            className="hidden xl:block shrink-0 overflow-y-auto"
            style={{ width: 360, borderLeft: '1px solid var(--line)', background: 'var(--line-subtle)', padding: 24 }}
          >
            <ServiceLivePreview formData={formData} setupLabel={setupLabel} />
          </aside>
        </div>
      )}

      {leaveModal}
    </div>
  );
};

export default ServiceEditorPage;

// ============================================================
// Sections
// ============================================================

export type Editor = ReturnType<typeof useServiceEditor>;

export function BadgeField({ editor }: { editor: Editor }) {
  const { formData, updateField, badgeServerIssue } = editor;
  const suggested = suggestedBadge(formData.subtype);
  const SuggestedIcon = badgeOption(suggested).icon;
  const options = [
    { value: '', label: `Suggested (${badgeOption(suggested).label})`, icon: <SuggestedIcon size={16} /> },
    ...SERVICE_BADGE_OPTIONS.map((o) => ({ value: o.key, label: o.label, icon: <o.icon size={16} /> })),
  ];
  return (
    <Field
      label="Badge"
      caption="The icon shown over the service photo in the app."
      error={badgeServerIssue ?? undefined}
    >
      <IconSelect
        ariaLabel="Badge"
        value={formData.badge}
        options={options}
        onChange={(v) => updateField('badge', v as ServiceBadgeKey | '')}
      />
    </Field>
  );
}

function DetailsSection({ editor }: { editor: Editor }) {
  const { formData, updateField } = editor;
  // SAM-198 editor v2: no "Section in the app" here — the category belongs to the business (7b).
  // service_group_id is still loaded and saved untouched. Same for the legacy "Catalogue type"
  // (service_type_id): the badge and the type/setup replace it, so it has no field here.
  return (
    <>
      <SectionCard title="Name and description" icon={<Briefcase size={14} />}>
        <Grid2>
          <Field label="Name (EN)" required={formData.name_en.trim() === ''}>
            <input
              value={formData.name_en}
              onChange={(e) => updateField('name_en', e.target.value)}
              placeholder="Service name (English)"
            />
          </Field>
          <Field label="Name (ES)">
            <input
              value={formData.name_es}
              onChange={(e) => updateField('name_es', e.target.value)}
              placeholder="Nombre del servicio (Español)"
            />
          </Field>
        </Grid2>
        <Grid2>
          <Field label="Description (EN)">
            <textarea
              value={formData.description_en}
              onChange={(e) => updateField('description_en', e.target.value)}
              placeholder="Service description (English)"
            />
          </Field>
          <Field label="Description (ES)">
            <textarea
              value={formData.description_es}
              onChange={(e) => updateField('description_es', e.target.value)}
              placeholder="Descripción del servicio (Español)"
            />
          </Field>
        </Grid2>
        <Grid2>
          <BadgeField editor={editor} />
        </Grid2>
      </SectionCard>

      <SectionCard title="Media" icon={<ImageIconPlaceholder />}>
        {/* SAM-1009 (option A): the service has a cover image and a colour; no logo of its own. */}
        <div className="mb-4">
          <S3ImageUpload
            label="Service Image"
            value={formData.background_img_url}
            onChange={(key) => updateField('background_img_url', key)}
            previewHeight="160px"
          />
        </div>
        <Grid2>
          <Field label="Background Color">
            <input
              value={formData.background_color}
              onChange={(e) => updateField('background_color', e.target.value)}
              placeholder="#D4D4D4"
            />
          </Field>
          <div className="flex items-end gap-2 pb-3.5">
            <div
              style={{
                width: 32, height: 32, borderRadius: 6, background: formData.background_color || '#D4D4D4',
                border: '1px solid var(--line)', flexShrink: 0,
              }}
            />
            <input
              type="color"
              value={formData.background_color || '#D4D4D4'}
              onChange={(e) => updateField('background_color', e.target.value)}
              style={{
                width: 32, height: 32, padding: 0, border: '1px solid var(--line)', borderRadius: 6, cursor: 'pointer',
              }}
            />
          </div>
        </Grid2>
      </SectionCard>
    </>
  );
}

function ImageIconPlaceholder() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="9" cy="9" r="2" />
      <path d="M21 15l-5-5L5 21" />
    </svg>
  );
}

// PoliciesSection (Cancellation policy + Danger zone) now lives in
// ./sections/PoliciesSection.tsx — see the import above. Keeping it here too,
// duplicated inline, would shadow that import and silently drop the SAM-198
// field-parity pass (waitlist/approval/payment/inside-outside-window/age/terms).
